/* eslint-disable array-bracket-newline, react-hooks/exhaustive-deps */

import { useCallback, useEffect, useState } from 'react';
import { enums, ITicker, ITickersSort } from '@kattana-io/common';
import { connect } from 'react-redux';
import {
  formatAddress,
  formatAPY,
  formatMobileTableData,
  IMobileTableScema,
} from '../../../../helpers/formatters';
import useTicker from '../../../../hooks/useTicker';
import useIsInViewport from './useIsInViewport';
import TableCardMobile from '../../../../molecules/Table/mobile/TableCard';
import { IRates } from '../../../../reducers/rates';
import Loader from '../../../common/Loader';
import {
  liquidityDescTickersSort,
  formatBaseVolume,
  formatLiquidity,
  volumeDescTickersSort,
} from '../helpers';
import {
  actionAddWatchlist,
  actionRemoveWatchlist,
} from '../../../../actions/watchlist';
import { switchCurrentTicker } from '../../../../Authlogic';
import {
  actionTickersNextPageModal,
  actionUpdateModal,
  actionUpdateModalParams,
} from '../../../../actions/modal_search';
import Text from '../../../../atoms/Text';
import TickerFooterMobile from './TickerFooter';
import { IMobileFilterProps } from '../../../SearchTable/SearchTable';
import DailyPriceChangeMobile from '../../../Watchlist/mobile/DailyPriceChange';
import PairIcons from '../../../../molecules/PairIcons';
import { IStore } from '../../../../reducers';

import './styles.scss';

interface ITickersPanelProps {
  tickers: any;
  watchlistTickers: ITicker[];
  currentId: number;
  search: string;
  inputSearch: string;
  exchanges: string[];
  chains: string[];
  items: ITicker[];
  loading: boolean;
  isNextPageLoading: boolean;
  page: number;
  rates: IRates;
  logged: boolean;
  actionRemoveWatchlist: Function;
  actionAddWatchlist: Function;
  actionUpdateModal: (value: any) => void;
  actionUpdateModalParams: (exchange: any) => void;
  actionTickersNextPageModal: Function;
  onMount: (data: IMobileFilterProps) => void;
  onBack: () => void;
}

const TickerTableMobile = (props: ITickersPanelProps) => {
  const { items, inputSearch, onMount } = props;

  const [sort, setSort] = useState<null | ITickersSort>(
    liquidityDescTickersSort
  );
  const [watchlistViewIds, setWatchlistViewIds] = useState<number[]>([]);
  const [lastElementRef, setLastElementRef] = useState<HTMLDivElement | null>(
    null
  );
  const lastIsOnScreen = useIsInViewport(lastElementRef, true);

  const handleRect = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      setLastElementRef(node);
    }
  }, []);

  const onTickersUpdate = () => props.actionUpdateModal({ tickers: items });

  useEffect(() => {
    if (!props.chains.length && props.exchanges.length) {
      setSort(volumeDescTickersSort);
    } else {
      setSort(liquidityDescTickersSort);
    }
  }, [props.exchanges, props.chains]);

  const setChainExchange = useCallback(
    (data: { items: string[]; isChain?: boolean }) => {
      if (data.isChain) {
        props.actionUpdateModalParams({ chains: data.items });
      } else {
        props.actionUpdateModalParams({ exchanges: data.items });
      }
    },
    [props]
  );

  useEffect(() => {
    props.actionUpdateModalParams({ search: inputSearch || '', mobile: true });
  }, [inputSearch]);

  useEffect(() => {
    onMount({
      setFilterValue: setChainExchange,
    });
  }, [onMount, setChainExchange]);

  /**
   * if items change, then put them into redux
   */
  useEffect(() => {
    if (items !== props.items) {
      onTickersUpdate();
    }
  }, [items]);

  /**
   * Sort trigger call to server
   */
  useEffect(() => {
    if (sort) {
      props.actionUpdateModalParams({ sort });
    }
  }, [sort]);

  /**
   * Trigger next page if reaches the last element
   */
  useEffect(() => {
    if (lastIsOnScreen) {
      props.actionTickersNextPageModal({
        exchanges: props.exchanges,
        chains: props.chains,
        search: props.search,
      });
    }
  }, [lastIsOnScreen]);

  useEffect(() => {
    if (watchlistViewIds.length === 0) {
      const ids = props.watchlistTickers.map((ticker) => ticker.id);
      setWatchlistViewIds(ids);
    }
  }, [props.watchlistTickers]);

  const handleCardClick = useCallback(
    (ticker: ITicker) => {
      switchCurrentTicker(ticker);
      props.onBack();
    },
    [props]
  );

  const schema: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'pair',
        parser: (_, row) => {
          const item = useTicker(row);
          return (
            <div className='ticker__cell' style={{ height: 40 }}>
              <PairIcons
                chain={item.chain}
                pair={item.name}
                base_address={item.base_address}
                quote_address={item.quote_address}
                base_slug={item.base_slug}
                quote_slug={item.quote_slug}
              />
              <Text size={14} className='text--weight-500 text--grey-400'>
                {item.name}
              </Text>
            </div>
          );
        },
      },
      item_2: {
        accessor: 'price',
        parser: (_, row) => <DailyPriceChangeMobile ticker={row} />,
      },
    },
    row_2: {
      item_1: {
        name: 'Liquidity',
        accessor: 'liquidity',
        parser: (value, row) =>
          row.kind === enums.ETickerKind.DEX ? formatLiquidity(value) : '',
      },
      item_2: {
        name: 'Volume',
        accessor: 'volume',
        parser: (value) => formatBaseVolume(value),
      },
    },
    row_3: {
      item_1: {
        name: 'Contract',
        accessor: 'base_address',
        parser: (value, row) =>
          row.kind === enums.ETickerKind.DEX ? formatAddress(value) : '',
        color: 'navy',
      },
      item_2: {
        name: 'APY',
        accessor: 'annual_percentage_yield',
        parser: (value) => (!value ? '' : formatAPY(value)),
      },
    },
  };

  const renderFooter = ({ data }: { data: ITicker }) => {
    const isInViewWatchlist = watchlistViewIds.includes(data.id);
    const isInWatchlist = props.watchlistTickers.some(
      (ticker) => ticker.id === data.id
    );

    const onToggleWatchlist = () => {
      if (props.logged) {
        if (isInViewWatchlist) {
          const newIds = watchlistViewIds.filter((id) => id !== data.id);
          setWatchlistViewIds(newIds);
        } else {
          const newIds = [...watchlistViewIds, data.id];
          setWatchlistViewIds(newIds);
        }
      }

      if (isInWatchlist) {
        props.actionRemoveWatchlist(data.id);
      } else {
        props.actionAddWatchlist(data);
      }
    };

    return (
      <TickerFooterMobile
        exchange={data.exchange}
        chain={data.chain}
        isInViewWatchlist={isInViewWatchlist}
        onToggleWatchlist={onToggleWatchlist}
      />
    );
  };

  const footerData = {};

  const parsedData =
    !props.loading && items.length
      ? formatMobileTableData({
        data: items,
        schema,
        getColorForItem: () => 'grey',
        renderFooter,
        footerData,
        handleCardClick,
        keyName: ['id'],
        spaceBetween: true,
      })
      : undefined;

  return (
    <>
      {props.loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !items.length ? (
        <div className='centrify'>No data found</div>
      ) : (
        <>
          {parsedData?.map((row, index) => (
            <TableCardMobile
              key={row.key}
              ref={index === parsedData.length - 1 ? handleRect : undefined}
              data={row}
            />
          ))}
          {props.isNextPageLoading && (
            <div className='backdrop'>
              <Loader />
            </div>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IStore) => ({
  rates: state.rates.items,
  tickers: state.tickers,
  watchlistTickers: state.watchlist.watched,
  currentId: state.current.id,
  search: state.modal_search.search,
  exchanges: state.modal_search.exchanges,
  chains: state.modal_search.chains,
  items: state.modal_search.tickers,
  loading: state.modal_search.loading,
  page: state.modal_search.page,
  isNextPageLoading: state.modal_search.isNextPageLoading,
  logged: state.auth.loggedIn,
});

export default connect(mapStateToProps, {
  actionAddWatchlist,
  actionRemoveWatchlist,
  actionUpdateModalParams,
  actionTickersNextPageModal,
  actionUpdateModal,
})(TickerTableMobile);
